import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { detectSource, type ImportJob, type SourceType } from '@dishly/shared';
import { ApiError, isNetworkError } from '../../lib/api.ts';
import { canReadClipboard, clipboardLink } from '../../lib/clipboard.ts';
import { importKeys, isActive, retryImport, startImport, useImports } from '../../lib/imports.ts';
import { recipeKeys, uploadImage } from '../../lib/recipes.ts';
import { m } from '../../paraglide/messages.js';
import { Banner } from '../../ui/Banner.tsx';
import { BottomSheet } from '../../ui/BottomSheet.tsx';
import { Button, buttonClass } from '../../ui/Button.tsx';
import { Icon, type IconName } from '../../ui/Icon.tsx';
import { IconButton } from '../../ui/IconButton.tsx';
import { ImportRow, type ImportRowState } from '../../ui/ImportRow.tsx';
import { GroupLabel } from '../../ui/SectionHeader.tsx';
import { TextArea, TextField } from '../../ui/TextField.tsx';
import { sourceLabel } from '../recipes/labels.ts';
import './ImportSheet.css';

export interface ImportPrefill {
  url?: string;
  text?: string;
  /** The link came from the clipboard (shown in the help line). */
  fromClipboard?: boolean;
}

const VIDEO: SourceType[] = ['youtube', 'instagram', 'tiktok', 'x', 'facebook'];

const sourceIcon = (type: SourceType): IconName =>
  VIDEO.includes(type) ? 'video' : type === 'text' ? 'text' : type === 'image' ? 'image' : 'link';

/** A link shortened for the row title until the real title is known. */
const shortUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60);

/** Wording for a failed import, by error kind. */
function failure(job: ImportJob): string {
  const kind = job.errorKind ?? '';
  if (kind === 'no_recipe' || kind === 'no_words') return m.import_fail_no_recipe();
  if (kind === 'supadata_missing') return m.import_fail_supadata_missing();
  if (kind === 'supadata_quota') return m.import_fail_supadata_quota();
  if (kind === 'not_configured' || kind === 'auth' || kind === 'supadata_auth') {
    return m.import_fail_keys();
  }
  if (kind === 'budget') return m.import_fail_budget();
  if (kind === 'refusal') return m.import_fail_refusal();
  if (kind === 'not_found') return m.import_fail_not_found();
  if (kind.startsWith('fetch_') || kind === 'not_html') return m.import_fail_fetch();
  return m.import_fail_generic();
}

function rowState(
  job: ImportJob,
  queuedAhead: number,
): {
  state: ImportRowState;
  step: number;
  status: string;
} {
  const video = VIDEO.includes(job.sourceType);
  switch (job.status) {
    case 'queued':
      return {
        state: 'queued',
        step: 0,
        status:
          job.error !== null
            ? m.import_status_retrying()
            : queuedAhead > 0
              ? m.import_status_queued_after({ count: queuedAhead })
              : m.import_status_queued(),
      };
    case 'fetching':
      return {
        state: 'active',
        step: 0,
        status: video
          ? m.import_status_fetching_video()
          : job.kind === 'url'
            ? m.import_status_fetching_page()
            : m.import_status_reading(),
      };
    case 'extracting':
      return { state: 'active', step: 2, status: m.import_status_writing() };
    case 'matching':
      return { state: 'active', step: 3, status: m.import_status_matching() };
    case 'done':
      return {
        state: 'done',
        step: 5,
        status:
          job.recipeReviewed === false ? m.import_status_done_review() : m.import_status_done(),
      };
    case 'failed':
      return { state: 'failed', step: 1, status: failure(job) };
  }
}

function ImportList({ onNavigate }: { onNavigate: () => void }) {
  const imports = useImports();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: retryImport,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: importKeys.list }),
  });
  const jobs = imports.data ?? [];
  if (jobs.length === 0) return null;
  const queued = jobs.filter((j) => j.status === 'queued').reverse();
  return (
    <section className="import-sheet__list" aria-labelledby="imports-title">
      <GroupLabel as="h3" id="imports-title">
        {m.import_list({ count: jobs.length })}
      </GroupLabel>
      {jobs.some(isActive) && <p className="import-sheet__hint">{m.import_can_close()}</p>}
      <ul aria-live="polite" aria-label={m.import_list({ count: jobs.length })}>
        {jobs.map((job) => {
          const { state, step, status } = rowState(job, queued.indexOf(job));
          const title =
            job.title ??
            (job.url
              ? shortUrl(job.url)
              : job.kind === 'image'
                ? m.import_title_photos()
                : m.import_title_text());
          let action = null;
          if (job.status === 'done' && job.recipeId) {
            action = (
              <Link
                to="/recipes/$id"
                params={{ id: job.recipeId }}
                className={buttonClass({ variant: 'ghost', size: 'm' })}
                onClick={onNavigate}
              >
                {job.recipeReviewed === false ? m.import_review() : m.import_open()}
              </Link>
            );
          } else if (job.status === 'failed') {
            action = (
              <Button
                variant="ghost"
                size="m"
                loading={retry.isPending && retry.variables === job.id}
                onClick={() => retry.mutate(job.id)}
              >
                {m.action_retry()}
              </Button>
            );
          }
          return (
            <ImportRow
              key={job.id}
              icon={sourceIcon(job.sourceType)}
              title={title}
              status={status}
              state={state}
              step={step}
              action={action}
            />
          );
        })}
      </ul>
    </section>
  );
}

/**
 * The Import sheet (design/reference/ImportSheet.dc.html): a link (pre-filled from the
 * clipboard), pasted text or 1–5 photos, and every import's pipeline state below.
 */
export function ImportSheet({
  open,
  prefill,
  onClose,
}: {
  open: boolean;
  prefill: ImportPrefill;
  onClose: () => void;
}) {
  return (
    <BottomSheet open={open} onClose={onClose} title={m.import_title()} detent="large">
      {open && <ImportBody key={JSON.stringify(prefill)} prefill={prefill} onClose={onClose} />}
    </BottomSheet>
  );
}

function ImportBody({ prefill, onClose }: { prefill: ImportPrefill; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'link' | 'text'>(prefill.text ? 'text' : 'link');
  const [url, setUrl] = useState(prefill.url ?? '');
  const [text, setText] = useState(prefill.text ?? '');
  const [duplicate, setDuplicate] = useState<{ recipeId: string; title: string } | null>(null);
  const [pasted, setPasted] = useState<string | null>(null);
  const photos = useRef<HTMLInputElement>(null);

  const submit = useMutation({
    mutationFn: startImport,
    onSuccess: async (created) => {
      if (created.duplicate) {
        setDuplicate({ recipeId: created.recipeId, title: created.title });
        return;
      }
      setDuplicate(null);
      setUrl('');
      setText('');
      setMode('link');
      await queryClient.invalidateQueries({ queryKey: importKeys.list });
      await queryClient.invalidateQueries({ queryKey: recipeKeys.list() });
    },
  });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const ids = [];
      for (const file of files.slice(0, 5)) ids.push((await uploadImage(file)).id);
      return startImport({ imageIds: ids });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: importKeys.list }),
  });

  const error = submit.error ?? upload.error;
  const detected = url.trim() ? detectSource(url) : null;
  const help =
    detected && detected !== 'text'
      ? (prefill.fromClipboard && url === prefill.url) || url === pasted
        ? m.import_from_clipboard({ source: sourceLabel[detected]() })
        : m.import_detected({ source: sourceLabel[detected]() })
      : m.import_link_help();

  if (mode === 'text') {
    return (
      <div className="import-sheet">
        <div className="import-sheet__subhead">
          <IconButton icon="chevron-left" label={m.import_back()} onClick={() => setMode('link')} />
          <h3 className="import-sheet__subtitle">{m.import_paste_text()}</h3>
        </div>
        <TextArea
          label={m.import_text_label()}
          hideLabel
          rows={10}
          value={text}
          autoFocus
          placeholder={m.import_text_placeholder()}
          help={m.import_text_help()}
          onChange={(e) => setText(e.target.value)}
        />
        {error && <ErrorBanner error={error} />}
        <Button
          variant="primary"
          block
          disabled={!text.trim()}
          loading={submit.isPending}
          onClick={() => submit.mutate({ text })}
        >
          {m.import_write_recipe()}
        </Button>
        <ImportList onNavigate={onClose} />
      </div>
    );
  }

  return (
    <div className="import-sheet">
      <form
        className="import-sheet__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) submit.mutate({ url });
        }}
      >
        <TextField
          label={m.import_link()}
          hideLabel
          type="url"
          inputMode="url"
          icon="link"
          placeholder={m.import_link_placeholder()}
          data-autofocus
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setDuplicate(null);
          }}
          help={help}
          trailing={
            url ? (
              <IconButton
                icon="close"
                iconSize="s"
                label={m.field_clear()}
                onClick={() => setUrl('')}
              />
            ) : canReadClipboard() ? (
              <IconButton
                icon="clipboard"
                iconSize="s"
                label={m.import_paste_link()}
                onClick={() =>
                  void clipboardLink().then((link) => {
                    if (!link) return;
                    setUrl(link);
                    setPasted(link);
                    setDuplicate(null);
                  })
                }
              />
            ) : undefined
          }
        />
        {duplicate && (
          <Banner variant="info" icon="book">
            {m.import_duplicate({ title: duplicate.title })}{' '}
            <Link
              to="/recipes/$id"
              params={{ id: duplicate.recipeId }}
              className="import-sheet__inline-link"
              onClick={onClose}
            >
              {m.import_open()}
            </Link>{' '}
            ·{' '}
            <button
              type="button"
              className="import-sheet__inline-link"
              onClick={() => submit.mutate({ url, force: true })}
            >
              {m.import_again()}
            </button>
          </Banner>
        )}
        {error && <ErrorBanner error={error} />}
        <Button
          type="submit"
          variant="primary"
          block
          disabled={!url.trim()}
          loading={submit.isPending}
        >
          {m.import_button()}
        </Button>
      </form>
      <div className="import-sheet__options">
        <button type="button" className="import-option" onClick={() => setMode('text')}>
          <Icon name="text" />
          {m.import_paste_text()}
        </button>
        <button
          type="button"
          className="import-option"
          disabled={upload.isPending}
          onClick={() => photos.current?.click()}
        >
          {upload.isPending ? (
            <span className="spinner" aria-hidden="true" />
          ) : (
            <Icon name="image" />
          )}
          {m.import_photos()}
        </button>
        <input
          ref={photos}
          type="file"
          accept="image/*"
          multiple
          hidden
          aria-label={m.import_photos()}
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            if (files.length > 0) upload.mutate(files);
            e.target.value = '';
          }}
        />
      </div>
      <ImportList onNavigate={onClose} />
    </div>
  );
}

function ErrorBanner({ error }: { error: Error }) {
  const message = isNetworkError(error)
    ? m.import_offline()
    : error instanceof ApiError && error.status === 400
      ? m.import_invalid()
      : m.error_generic();
  return <Banner variant={isNetworkError(error) ? 'offline' : 'error'}>{message}</Banner>;
}
